import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, limit, orderBy } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { User } from '../types/user.types';
import { getErrorMessage, getFirebaseErrorCode } from '@utils/errors';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [indexBuilding, setIndexBuilding] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersQuery = query(
          collection(firestore, 'users'),
          where('isActive', '==', true),
          where('profileComplete', '==', true),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        
        const snapshot = await getDocs(usersQuery);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[];
        
        setUsers(usersData);
      } catch (err: unknown) {
        console.error('Error fetching users:', err);
        const code = getFirebaseErrorCode(err);
        if (code === 'failed-precondition' && /index.*building/i.test(getErrorMessage(err))) {
          setIndexBuilding(true);
          setError('INDEX_BUILDING');
        } else if (code === 'permission-denied') {
          setError('PERMISSION_DENIED');
        } else {
          setError('FAILED');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return { users, loading, error, indexBuilding };
};

export const useUser = (userId: string | undefined) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchUser = async () => {
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(firestore, 'users', userId));
        
        if (userDoc.exists()) {
          setUser({ id: userDoc.id, ...userDoc.data() } as User);
        } else {
          setError('User not found');
        }
      } catch (err: unknown) {
        console.error('Error fetching user:', err);
        if (getFirebaseErrorCode(err) === 'permission-denied') {
          setError('PERMISSION_DENIED');
        } else {
          setError('FAILED');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  return { user, loading, error };
};
